import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Plus, BookOpen, Crown } from "lucide-react";
import { login } from "@/lib/api";

export default function Series() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/series").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/series", { title, description });
      toast.success(data.is_founding ? "Founding Series badge unlocked!" : "Series created");
      setOpen(false);
      setTitle(""); setDescription("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">Series</div>
          <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">Multi-story worlds</h1>
          <p className="mt-3 text-slate-400 max-w-2xl">Group your stories into a growing saga. The first 10 series ever created wear a <span className="text-amber-300">Founding Series</span> badge — forever.</p>
        </div>
        {user ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-series-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full h-11 px-5">
                <Plus className="h-4 w-4 mr-1" /> Start a series
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F1117] border-white/10">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Start a new series</DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Group stories under one saga. The first 10 series ever earn a Founding Series badge.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Series title</Label>
                  <Input data-testid="series-title" value={title} onChange={e => setTitle(e.target.value)} maxLength={120} className="mt-1 bg-[#07080A] border-white/10" />
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Description</Label>
                  <Textarea data-testid="series-desc" value={description} onChange={e => setDescription(e.target.value)} maxLength={500} className="mt-1 min-h-[120px] bg-[#07080A] border-white/10" />
                </div>
                <Button data-testid="series-submit" type="submit" disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11">
                  {busy ? "Creating…" : "Create series"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        ) : (
          <Button onClick={login} className="rounded-full bg-amber-500 text-black font-semibold h-11 px-5">Sign in to start</Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-500">
          No series yet. Be the first — earn the <span className="text-amber-300">Founding Series</span> badge.
        </div>
      ) : (
        <div data-testid="series-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {items.map((s) => (
            <Link key={s.series_id} to={`/series/${s.series_id}`} data-testid={`series-card-${s.series_id}`} className="rounded-2xl border border-white/10 bg-[#0F1117] p-6 glow-hover block">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">by {s.author_name}</span>
                {s.is_founding && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300">
                    <Crown className="h-3 w-3" /> Founding
                  </span>
                )}
              </div>
              <h3 className="font-serif text-xl font-semibold mb-2 line-clamp-2">{s.title}</h3>
              <p className="text-sm text-slate-400 line-clamp-3">{s.description}</p>
              <div className="mt-4 flex items-center gap-2 text-xs text-amber-300">
                <BookOpen className="h-3.5 w-3.5" /> {s.story_count} {s.story_count === 1 ? "story" : "stories"}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
